import { useRef } from 'react'
import { FaBriefcase, FaEnvelope, FaArrowLeft } from 'react-icons/fa'
import VariableProximity from '../components/VariableProximity'
import { useNavigate } from 'react-router-dom'
import GlassSurface from '../components/GlassSurface'

const Careers = () => {
  const careersHeadingRef = useRef(null)
  const navigate = useNavigate()

  return (
    <div>
      {/* Header */}
      <section className="section-padding bg-transparent backdrop-blur-sm text-white pt-32 md:pt-36 border-b border-dark-700/30">
        <div className="container-custom text-center">
          <div
            ref={careersHeadingRef}
            style={{ position: 'relative' }}
            className="mb-6"
          >
            <VariableProximity
              label="Careers"
              className="text-5xl md:text-6xl font-bold"
              fromFontVariationSettings="'wght' 400, 'opsz' 9"
              toFontVariationSettings="'wght' 1000, 'opsz' 40"
              containerRef={careersHeadingRef}
              radius={100}
              falloff="linear"
              style={{ color: 'white' }}
            />
          </div>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Join our team and help shape the future of freight consultancy services.
          </p>
        </div>
      </section>

      {/* No Vacancies Section */}
      <section className="section-padding bg-transparent">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <GlassSurface
              width="100%"
              borderRadius={24}
              blur={10}
              displace={0.35}
              distortionScale={-110}
              redOffset={-2}
              greenOffset={6}
              blueOffset={12}
              backgroundOpacity={0.12}
              saturation={1.2}
              opacity={0.9}
              mixBlendMode="screen"
              className="transition-all hover:scale-[1.01] hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6),0_0_40px_rgba(0,255,195,0.3)]"
            >
              <div className="p-8 md:p-12 lg:p-16 text-center">
                <div className="flex justify-center mb-8">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-accent-400/25 to-accent-400/10 flex items-center justify-center border-2 border-accent-400/50 shadow-[0_0_20px_rgba(0,255,195,0.3)]">
                    <FaBriefcase className="text-accent-400" size={40} />
                  </div>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                  Currently Not Hiring
                </h2>
                <div className="space-y-4 mb-10">
                  <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
                    We are not currently accepting applications for any positions. 
                    We appreciate your interest in joining <span className="text-white font-semibold">EW Logistics & IT Solution LLC</span>.
                  </p>
                  <p className="text-base md:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
                    Please check back in the future for career opportunities. 
                    We are always looking for talented individuals who share our commitment 
                    to excellence in freight consultancy services.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    onClick={() => navigate('/')}
                    className="btn-secondary flex items-center justify-center min-w-[160px]"
                  >
                    <FaArrowLeft className="mr-2" />
                    Back to Home
                  </button>
                  <a
                    href="mailto:hr@ewlogisticsusa.com"
                    className="btn-primary flex items-center justify-center min-w-[160px]"
                  >
                    <FaEnvelope className="mr-2" />
                    Contact HR
                  </a>
                </div>
              </div>
            </GlassSurface>
          </div>
        </div>
      </section>

      {/* Future Opportunities Section */}
      <section className="section-padding bg-transparent">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-white mb-6">
              Future Opportunities
            </h3>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              When positions become available, we look for individuals who are passionate 
              about logistics, customer service, and building lasting partnerships with our carriers.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <GlassSurface
                width="100%"
                borderRadius={16}
                blur={8}
                displace={0.3}
                distortionScale={-100}
                redOffset={-2}
                greenOffset={6}
                blueOffset={12}
                backgroundOpacity={0.08}
                saturation={1.15}
                opacity={0.85}
                mixBlendMode="screen"
                className="transition-all hover:scale-105"
                style={{ minHeight: '140px' }}
              >
                <div className="p-6 h-full flex flex-col justify-center items-center text-center text-white">
                  <div className="text-3xl font-bold text-accent-400 mb-2">Dispatch</div>
                  <div className="text-gray-300">Specialists</div>
                </div>
              </GlassSurface>
              <GlassSurface
                width="100%"
                borderRadius={16}
                blur={8}
                displace={0.3}
                distortionScale={-100}
                redOffset={-2}
                greenOffset={6}
                blueOffset={12}
                backgroundOpacity={0.08}
                saturation={1.15}
                opacity={0.85}
                mixBlendMode="screen"
                className="transition-all hover:scale-105"
                style={{ minHeight: '140px' }}
              >
                <div className="p-6 h-full flex flex-col justify-center items-center text-center text-white">
                  <div className="text-3xl font-bold text-accent-400 mb-2">Customer</div>
                  <div className="text-gray-300">Support</div>
                </div>
              </GlassSurface>
              <GlassSurface
                width="100%"
                borderRadius={16}
                blur={8}
                displace={0.3}
                distortionScale={-100}
                redOffset={-2}
                greenOffset={6}
                blueOffset={12}
                backgroundOpacity={0.08}
                saturation={1.15}
                opacity={0.85}
                mixBlendMode="screen"
                className="transition-all hover:scale-105"
                style={{ minHeight: '140px' }}
              >
                <div className="p-6 h-full flex flex-col justify-center items-center text-center text-white">
                  <div className="text-3xl font-bold text-accent-400 mb-2">Sales</div>
                  <div className="text-gray-300">Representatives</div>
                </div>
              </GlassSurface>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Careers

